import React, { useEffect, useState } from "react";
import { Product } from "../product-step/types";
import { AddOnCard } from "./AddOnCard";
import { BookingLanguage } from "@/types/booking";

interface AddOnSectionProps {
  title: string;
  products: Product[];
  selectedAddOns: string[];
  onAddOnToggle: (productId: string) => void;
  language: BookingLanguage;
}

/**
 * One recommended section (blood tests or imaging), every add-on shown.
 * `products` is expected to arrive already ordered (recommended-first). The
 * overflow used to collapse behind a per-section "View more (N)" toggle;
 * removed so the whole catalog is on screen at once.
 */
/** Matches the `duration-300` fade on the exiting card below. */
const EXIT_MS = 300;

export const AddOnSection: React.FC<AddOnSectionProps> = ({
  title,
  products,
  selectedAddOns,
  onAddOnToggle,
  language,
}) => {
  // Add-ons bundled into a newly attached scan drop out of `products`. Keep
  // them on screen for one fade-out so the list visibly loses them instead of
  // silently reshuffling.
  const [display, setDisplay] = useState(products);
  const [exitingIds, setExitingIds] = useState<string[]>([]);

  useEffect(() => {
    const removed = display
      .filter((p) => !products.some((n) => n.id === p.id))
      .map((p) => p.id);
    if (removed.length === 0) {
      setDisplay(products);
      setExitingIds([]);
      return;
    }
    setExitingIds(removed);
    const timer = setTimeout(() => {
      setDisplay(products);
      setExitingIds([]);
    }, EXIT_MS);
    return () => clearTimeout(timer);
    // `display` is the previous frame's list — including it would re-run this
    // on its own update and cut the exit animation short.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  if (display.length === 0) return null;

  return (
    <div>
      <h2 className="text-sm font-medium text-neutral-400 uppercase leading-5 tracking-[0.1px] pt-2 pl-4 mb-2">
        {title}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {display.map((addOn) => {
          const isExiting = exitingIds.includes(addOn.id);
          return (
            <div
              key={addOn.id}
              className={`transition-all duration-300 ease-out ${
                isExiting
                  ? "pointer-events-none scale-95 opacity-0"
                  : "scale-100 opacity-100"
              }`}
            >
              <AddOnCard
                product={addOn}
                isSelected={selectedAddOns.includes(addOn.id)}
                onToggle={() => onAddOnToggle(addOn.id)}
                language={language}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
